import { useField } from 'react-final-form';
import { ResourcePairFieldProps } from './_components/ResourcePairField';

import {
  RffInput,
  RffSlider,
  useControlsGroupState,
} from '@cast/shared-rff-ui';

import { Typography, Stack, tokens } from '@cast/design-system';

const getCpuStallThresholdCopy = (value: number): string => {
  if (value <= 5)
    return 'Not recommended. Healthy workloads may trigger unnecessary scaling.';
  if (value <= 10)
    return 'Suitable for latency-sensitive production workloads.';
  if (value <= 30)
    return 'Balanced. Trades some latency headroom for lower resource cost.';
  if (value <= 50)
    return 'Suitable for non-critical workloads where contention is acceptable.';
  return 'Significant performance degradation before scaling occurs.';
};

const getPodThresholdCopy = (value: number): string => {
  if (value <= 10) return 'A single outlier pod can trigger scaling.';
  if (value <= 30) return 'Responsive to early signs of widespread contention.';
  if (value <= 60)
    return 'Scale only when a significant portion of pods is affected.';
  return 'Scale only when contention is widespread.';
};

export const StallDetectionField = ({
  LabelComponent,
}: Pick<ResourcePairFieldProps, 'LabelComponent'>) => {
  const Label = LabelComponent || Typography;

  const { name: cpuStallFieldName = '' } = useControlsGroupState({
    name: 'cpuStallThresholdPercentage',
  });
  const { name: podThresholdFieldName = '' } = useControlsGroupState({
    name: 'minPressuredPodPercentage',
  });

  const cpuStallValue = useField<number>(cpuStallFieldName).input.value;
  const podThresholdValue = useField<number>(podThresholdFieldName).input.value;

  return (
    <Stack gap={16} data-testid="stall-detection-field">
      <Stack gap={8}>
        <Label variant="A1">CPU Stall Threshold</Label>
        <Typography
          variant="P12R"
          sx={{ color: tokens.theme.fg.structural.secondary }}
        >
          Set the CPU stall percentage that triggers resource increases. When
          workloads can&apos;t get CPU time and stall exceeds this threshold,
          they&apos;ll be scaled up to reduce contention.
        </Typography>
      </Stack>
      <Stack gap={4}>
        <Stack direction="row" alignItems="center" gap={16}>
          <RffInput
            name="cpuStallThresholdPercentage"
            type="number"
            min={0}
            max={100}
            endAdornment="%"
            sx={{ width: 64 }}
            testId="cpu-stall-threshold-input"
          />
          <RffSlider
            name="cpuStallThresholdPercentage"
            min={0}
            max={100}
            sx={{ width: 'auto', flexGrow: 1 }}
          />
        </Stack>
        <Typography
          variant="P10R"
          sx={{ color: tokens.theme.fg.structural.secondary }}
        >
          {getCpuStallThresholdCopy(cpuStallValue)}
        </Typography>
      </Stack>

      <Stack gap={8}>
        <Label variant="A1">Pod Threshold</Label>
        <Typography
          variant="P12R"
          sx={{ color: tokens.theme.fg.structural.secondary }}
        >
          The percentage of pods that must exceed the stall threshold before
          scaling. This prevents scaling the entire workload when only a single
          pod or small subset is experiencing pressure.
        </Typography>
      </Stack>
      <Stack gap={4}>
        <Stack direction="row" alignItems="center" gap={16}>
          <RffInput
            name="minPressuredPodPercentage"
            type="number"
            min={0}
            max={100}
            endAdornment="%"
            sx={{ width: 64 }}
            testId="pod-threshold-input"
          />
          <RffSlider
            name="minPressuredPodPercentage"
            min={0}
            max={100}
            sx={{ width: 'auto', flexGrow: 1 }}
          />
        </Stack>
        <Typography
          variant="P10R"
          sx={{ color: tokens.theme.fg.structural.secondary }}
        >
          {getPodThresholdCopy(podThresholdValue)}
        </Typography>
      </Stack>
    </Stack>
  );
};
